<script setup lang="ts">
import { AUTH_QUERY_KEYS } from '~/queries/auth/token'

const nuxtApp = useNuxtApp()
const toast = useToast()

// Safely access $pwa - it may be undefined on initial load or if PWA module isn't loaded
const $pwa = computed(() => nuxtApp.$pwa)
const { $datadog, $pendo } = useNuxtApp()

const { user, loggedIn } = useUserSession()
const { clearCache } = usePdfCache()
const { ping, connectionState } = useUserAgent()

/* Clear PDF blob storage on app refresh */
onMounted(() => {
  clearCache()
})

watch(
  loggedIn,
  (val) => {
    if (!val) {
      clearCache()
      const queryCache = useQueryCache()
      queryCache.invalidateQueries({ key: AUTH_QUERY_KEYS.token })
      $datadog.clearUser()
    }
  },
  { immediate: true },
)

// Ping the UserAgent once both logged in and WebSocket is connected
watchEffect(() => {
  if (loggedIn.value && connectionState.value === 'connected') {
    ping()
  }
})

whenever(
  () => $pwa.value && 'needRefresh' in $pwa.value && $pwa.value.needRefresh,
  () => {
    const pwa = $pwa.value
    if (!pwa || !pwa.updateServiceWorker) return

    toast.add({
      title: 'New version available',
      description: 'Please refresh to use the new version',
      color: 'primary',
      progress: false,
      close: false,
      duration: 0,
      actions: [
        {
          label: 'Reload',
          variant: 'subtle',
          icon: 'i-ph-arrow-clockwise',
          onClick: () => {
            pwa.updateServiceWorker?.()
          },
        },
      ],
    })
  },
  {
    immediate: true,
  },
)

watch(
  [user],

  ([currentUser]) => {
    if (!currentUser) {
      $datadog.clearUser()
      return
    }

    const { email, name, sub, org_id } = currentUser
    const roles = currentUser['https://auth.rialtic.io/roles']

    $datadog.setUser({
      id: sub,
      name,
      email,
      roles: roles?.join(', ') ?? '',
      email_domain: email.split('@')[1],
      org_id,
    })

    if (!$pendo) return

    // @ts-expect-error - nuxt-pendo types seem to be outdated, this is working as expected
    $pendo.identify({
      visitor: {
        id: email,
        email,
        full_name: name,
        role: roles[0] ?? '',
        roles: roles?.join(', ') ?? '',
      },
      account: {
        id: email.split('@')[1],
        rialtic_app: 'admin',
      },
    })
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <UApp :toaster="{ expand: false }">
    <NuxtLoadingIndicator
      color="repeating-linear-gradient(to right,#6F7CCD 0%,#28B7F0 62%,#35CDDA 100%)"
    />
    <NuxtPwaManifest />
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </UApp>
</template>
